/**
 * Outage — frontend component for unplanned outage (J-X18).
 *
 * Triggered by global error boundary + /api/health polling failure
 * (3 consecutive 5xx or network errors).
 *
 * Features:
 *   - Auto-retries /api/health every 30s
 *   - On success: auto-reloads to current intended route
 *   - Mid-attempt-aware: if on /take/:token/*, shows "Your attempt is saved locally"
 *
 * Cross-cutting invariants:
 *   - Inv 12: no console.log
 *   - Inv 13: no `any`
 *   - Inv 14: no internal nav needed (user stays put until recovery)
 *   - Inv 15: atoms from @/components/ui barrel only
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { Eyebrow, Heading } from '@/components/ui';
import { ErrorPageChrome } from './ErrorPageChrome.tsx';

const HEALTH_ENDPOINT = '/api/health';
const RETRY_INTERVAL_MS = 30_000;

async function checkHealth(): Promise<boolean> {
  try {
    const res = await fetch(HEALTH_ENDPOINT, { method: 'GET', cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

export interface OutagePageProps {
  /** Override retry interval in ms (useful for tests). Defaults to 30000. */
  retryIntervalMs?: number;
}

export function OutagePage({ retryIntervalMs = RETRY_INTERVAL_MS }: OutagePageProps = {}): ReactElement {
  const location = useLocation();
  const isTakeRoute = location.pathname.startsWith('/take/');
  const [retryCount, setRetryCount] = useState(0);
  const [recovered, setRecovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    async function poll(): Promise<void> {
      const healthy = await checkHealth();
      if (healthy) {
        setRecovered(true);
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
        }
        // Auto-reload to current route after brief UX acknowledgment
        setTimeout(() => {
          window.location.reload();
        }, 1_500);
      } else {
        setRetryCount((c) => c + 1);
      }
    }

    void poll();
    intervalRef.current = setInterval(() => { void poll(); }, retryIntervalMs);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [retryIntervalMs]);

  if (recovered) {
    return (
      <ErrorPageChrome>
        <Eyebrow>Service restored</Eyebrow>
        <Heading level="h1" className="mt-6">
          We&apos;re back online.
        </Heading>
        <p className="mt-4 font-sans text-body-lg text-muted">
          Reloading your page now&hellip;
        </p>
      </ErrorPageChrome>
    );
  }

  return (
    <ErrorPageChrome>
      <Eyebrow>Service unavailable</Eyebrow>
      <Heading level="h1" className="mt-6">
        We&apos;re experiencing technical difficulties.
      </Heading>
      <p className="mt-4 font-sans text-body-lg text-muted">
        Our team is working on it. Refresh in a few minutes.
      </p>
      {isTakeRoute ? (
        <div className="mt-6 rounded-md border border-line bg-bg-warm px-4 py-3">
          <p className="font-sans text-body text-ink font-medium">
            Your attempt is saved locally.
          </p>
          <p className="mt-1 font-sans text-small text-muted">
            Please return shortly &mdash; your progress will be waiting for you.
          </p>
        </div>
      ) : null}
      <div className="mt-6 rounded-md border border-line bg-bg-warm px-4 py-3">
        <p className="font-mono text-mono text-muted">
          Retrying automatically every 30 seconds
          {retryCount > 0 ? ` · ${retryCount} attempt${retryCount === 1 ? '' : 's'} so far` : ''}
          &hellip;
        </p>
      </div>
    </ErrorPageChrome>
  );
}
